import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useMedplum } from '@medplum/react';
import { MEDPLUM_PROJECT_ID, MEDPLUM_RECAPTCHA_SITE_KEY } from '../config';
import { normalizeOperationOutcome } from '@medplum/core';
import { useNavigate } from 'react-router-dom';
import { setupPremiumTrial } from '../utils/subscription';

// Add reCAPTCHA type declaration
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Helper function to initialize reCAPTCHA
const initRecaptcha = (siteKey: string) => {
  // Check if the script is already loaded
  if (document.querySelector(`script[src*="recaptcha/api.js?render=${siteKey}"]`)) {
    return; // Script already exists
  }
  
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  document.head.appendChild(script);
};

// Helper function to get reCAPTCHA token
const getRecaptchaToken = async (siteKey: string): Promise<string> => {
  // Wait for grecaptcha to be available
  const checkRecaptchaLoaded = (): Promise<void> => {
    return new Promise((resolve) => {
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        resolve();
      } else {
        setTimeout(() => checkRecaptchaLoaded().then(resolve), 100);
      }
    });
  };

  await checkRecaptchaLoaded();
  
  return new Promise((resolve) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action: 'submit' })
        .then((token: string) => resolve(token))
        .catch(() => resolve(''));
    });
  });
};

const CTA: React.FC = () => {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    if (MEDPLUM_RECAPTCHA_SITE_KEY) {
      console.log('Initializing reCAPTCHA for CTA registration form...');
      initRecaptcha(MEDPLUM_RECAPTCHA_SITE_KEY);
      
      // Clean up the script when component unmounts
      return () => {
        const scripts = document.querySelectorAll(`script[src*="recaptcha/api.js"]`);
        scripts.forEach(script => {
          if (script.parentNode) {
            console.log('Removing reCAPTCHA script on unmount');
            script.parentNode.removeChild(script);
          }
        });
        
        // Also clean up any grecaptcha related elements
        const grecaptchaElements = document.querySelectorAll('.grecaptcha-badge');
        grecaptchaElements.forEach(element => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
      };
    }
  }, []);
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    const formData = new FormData(e.currentTarget);
    
    try {
      // Get form values
      const firstName = formData.get('firstName') as string;
      const lastName = formData.get('lastName') as string;
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;
      
      // Get reCAPTCHA token if site key is available
      let recaptchaToken = '';
      if (MEDPLUM_RECAPTCHA_SITE_KEY) {
        console.log('Getting reCAPTCHA token for CTA form submission...');
        recaptchaToken = await getRecaptchaToken(MEDPLUM_RECAPTCHA_SITE_KEY);
      }
      
      // Call Medplum to start new user registration
      const response = await medplum.startNewUser({
        projectId: MEDPLUM_PROJECT_ID,
        firstName,
        lastName,
        email,
        password,
        recaptchaSiteKey: MEDPLUM_RECAPTCHA_SITE_KEY,
        recaptchaToken,
      });
      
      // Handle successful response
      if (response.code) {
        // Process the code directly
        await medplum.processCode(response.code);
        setSuccess(true);
        // Redirect to home after successful registration
        setTimeout(() => navigate('/'), 1500);
      } else if (response.login) {
        // Start new patient flow if needed
        const patientResp = await medplum.startNewPatient({ 
          login: response.login,
          projectId: MEDPLUM_PROJECT_ID 
        });
        
        if (patientResp.code) {
          await medplum.processCode(patientResp.code);
          
          // Set up premium trial after patient is created
          try {
            const profile = medplum.getProfile();
            if (profile?.resourceType === 'Patient' && profile.id) {
              await setupPremiumTrial(medplum, profile.id);
              // Invalidate the profile cache to pick up the trial extensions
              medplum.invalidateAll();
              console.log('Premium trial set up successfully for CTA registration');
            }
          } catch (trialError) {
            console.error('Error setting up premium trial during CTA registration:', trialError);
            // Continue with registration even if trial setup fails
          }
          
          setSuccess(true);
          setTimeout(() => navigate('/'), 1500);
        }
      }
    } catch (err) {
      // Handle errors
      const outcome = normalizeOperationOutcome(err);
      setError(outcome.issue?.[0]?.details?.text || 'An error occurred during registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="cta-section" className="py-16 md:py-24 bg-gradient-to-r from-[#C2A4D6]/30 to-[#B3D9E6]/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 bg-[#C2A4D6]/20 p-8 md:p-12 flex flex-col justify-center">
              <div className="mb-6 inline-block bg-white p-3 rounded-full">
                <Heart className="w-8 h-8 text-[#F4B6A1]" />
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-['Poppins']">
                Begin Your Journey to Feeling <span className="text-[#C2A4D6]">Truly Heard</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 font-['Merriweather']">
                Join thousands who have discovered the power of being understood. 
                Start with a 7-day free trial and experience the difference for yourself.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#F4B6A1] rounded-full mr-2"></span>
                  <span className="text-gray-600">No commitment, cancel anytime</span>
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#F4B6A1] rounded-full mr-2"></span>
                  <span className="text-gray-600">5-conversation satisfaction guarantee</span>
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-[#F4B6A1] rounded-full mr-2"></span>
                  <span className="text-gray-600">Try Voice Connect features for 7 days</span>
                </li>
              </ul>
              
              {/* Show App Store download buttons */}
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">Or download our mobile app:</p>
                <div className="flex flex-wrap gap-3 items-center justify-center md:justify-start">
                  <a 
                    href="https://apps.apple.com/app/feelheard/id6746776325?platform=iphone"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <img 
                      src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      className="h-[36px] hover:opacity-90 transition-opacity"
                    />
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=me.feelheard"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <img 
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-[52px] hover:opacity-90 transition-opacity"
                    />
                  </a>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Create Your Account</h3>
              
              {success ? (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
                  Account created successfully! Redirecting you to your dashboard...
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      {error}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
                        placeholder="Jane"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
                        placeholder="Smith"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
                      placeholder="jane@example.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
                      placeholder="••••••••"
                      minLength={8}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">Password must be at least 8 characters</p>
                  </div>
                  
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-3 px-6 rounded-full text-lg font-medium transition-all transform hover:scale-105 shadow-md ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? 'Creating Account...' : 'Start 7-Day Free Trial'}
                    </button>
                  </div>
                </form>
              )}
              
              <p className="text-sm text-gray-500 mt-6 text-center">
                By signing up, you agree to our <a href="/terms" className="text-[#C2A4D6] hover:underline">Terms</a> and 
                acknowledge our <a href="/privacy" className="text-[#C2A4D6] hover:underline">Privacy Policy</a>.
              </p>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <a href="/signin" className="text-[#C2A4D6] hover:underline font-medium">
                    Sign in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;